import { useCallback, useEffect, useState } from "react";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { api, apiError, youTubeId } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import {
  AdminBanner,
  AdminButton,
  AdminCard,
  AdminField,
  AdminPageHead,
  AdminSpinner,
  AdminTable,
  Drawer,
  EmptyState,
  StatusBadge,
  adminInputClass,
} from "@/components/admin/AdminUi";
import MediaPicker from "@/components/admin/MediaPicker";

/* -------------------------------------------------------- Emerald Media */

const BLANK_MEDIA = {
  title: "",
  media_type: "youtube",
  url: "",
  description: "",
  duration: "",
  thumbnail_url: "",
  status: "draft",
  sort_order: 0,
};

export function EmeraldMedia() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [banner, setBanner] = useState({ status: "idle", message: "" });

  const load = useCallback(() => {
    api
      .get("/admin/emerald/media")
      .then((r) => setItems(r.data))
      .catch((e) => {
        setItems([]);
        setBanner({ status: "error", message: apiError(e) });
      });
  }, []);

  useEffect(load, [load]);

  const save = async () => {
    const payload = { ...editing, sort_order: Number(editing.sort_order) || 0 };
    delete payload.id;
    delete payload.created_at;
    delete payload.updated_at;
    try {
      if (editing.id) await api.put(`/admin/emerald/media/${editing.id}`, payload);
      else await api.post("/admin/emerald/media", payload);
      setBanner({ status: "ok", message: `"${editing.title}" saved.` });
      setEditing(null);
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/admin/emerald/media/${item.id}`);
      setBanner({ status: "ok", message: "Deleted." });
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="cms-emerald-media">
      <Seo title="Emerald Media / Videos | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead
        title="Emerald Media / Videos"
        subtitle="Manage the videos and media shown in the Emerald Honours section."
      >
        {can("emerald.content") && (
          <AdminButton variant="emerald" testId="cms-emerald-media-add" onClick={() => setEditing(BLANK_MEDIA)}>
            <Plus className="h-4 w-4" /> Add Media
          </AdminButton>
        )}
      </AdminPageHead>

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <AdminCard className="overflow-hidden">
        {items === null ? (
          <AdminSpinner />
        ) : items.length === 0 ? (
          <EmptyState message="No Emerald media added yet." testId="cms-emerald-media-empty" />
        ) : (
          <AdminTable columns={["Title", "Type", "Order", "Status", "Actions"]} testId="cms-emerald-media-table">
            {items.map((m) => (
              <tr key={m.id} className="hover:bg-slate-50/70" data-testid={`cms-emerald-media-row-${m.id}`}>
                <td className="px-5 py-4">
                  <p className="font-bold text-bka-navy">{m.title}</p>
                  <p className="truncate text-xs text-slate-500">{m.url}</p>
                </td>
                <td className="px-5 py-4 capitalize text-slate-500">{m.media_type}</td>
                <td className="px-5 py-4 text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <GripVertical className="h-3.5 w-3.5 text-slate-300" />
                    {m.sort_order}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <StatusBadge status={m.status} testId={`cms-emerald-media-status-${m.id}`} />
                </td>
                <td className="px-5 py-4">
                  <div className="flex gap-1.5">
                    <AdminButton
                      variant="ghost"
                      testId={`cms-emerald-media-edit-${m.id}`}
                      onClick={() => setEditing(m)}
                      className="px-3 py-1.5 text-xs"
                    >
                      Edit
                    </AdminButton>
                    {can("emerald.content") && (
                      <button
                        type="button"
                        onClick={() => remove(m)}
                        aria-label={`Delete ${m.title}`}
                        data-testid={`cms-emerald-media-delete-${m.id}`}
                        className="flex h-[34px] w-[34px] items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </AdminTable>
        )}
      </AdminCard>

      <Drawer
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Media" : "Add Media"}
        testId="cms-emerald-media-drawer"
      >
        {editing && (
          <div className="space-y-5">
            <AdminField label="Title" htmlFor="em-title" required>
              <input id="em-title" data-testid="cms-emerald-media-title" value={editing.title} onChange={set("title")} className={adminInputClass} />
            </AdminField>
            <AdminField label="Media Type" htmlFor="em-type">
              <select id="em-type" data-testid="cms-emerald-media-type" value={editing.media_type} onChange={set("media_type")} className={adminInputClass}>
                {["youtube", "video", "image", "article"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </AdminField>
            <AdminField label="URL" htmlFor="em-url" hint="Paste the full YouTube link or media URL.">
              <input id="em-url" data-testid="cms-emerald-media-url" value={editing.url || ""} onChange={set("url")} className={adminInputClass} placeholder="https://www.youtube.com/watch?v=…" />
            </AdminField>
            {editing.media_type === "youtube" && youTubeId(editing.url || "") && (
              <img
                src={`https://img.youtube.com/vi/${youTubeId(editing.url)}/hqdefault.jpg`}
                alt="Video thumbnail preview"
                className="h-40 w-full rounded-xl object-cover"
              />
            )}
            <AdminField label="Thumbnail Image (optional)">
              <MediaPicker
                kind="images"
                label=""
                value={editing.thumbnail_url}
                onChange={(url) => setEditing((f) => ({ ...f, thumbnail_url: url }))}
                buttonLabel="Choose or Upload Thumbnail"
              />
              <p className="mt-2 text-xs text-slate-500">
                Leave this empty to use the YouTube thumbnail automatically.
              </p>
            </AdminField>
            <AdminField label="Description" htmlFor="em-desc">
              <textarea id="em-desc" data-testid="cms-emerald-media-description" rows={3} value={editing.description || ""} onChange={set("description")} className={adminInputClass} />
            </AdminField>
            <div className="grid gap-5 sm:grid-cols-2">
              <AdminField label="Duration" htmlFor="em-duration" hint="e.g. 3:12">
                <input id="em-duration" data-testid="cms-emerald-media-duration" value={editing.duration || ""} onChange={set("duration")} className={adminInputClass} />
              </AdminField>
              <AdminField label="Display Order" htmlFor="em-order">
                <input id="em-order" data-testid="cms-emerald-media-order" type="number" value={editing.sort_order} onChange={set("sort_order")} className={adminInputClass} />
              </AdminField>
            </div>
            <AdminField label="Status" htmlFor="em-status">
              <select id="em-status" data-testid="cms-emerald-media-status-select" value={editing.status} onChange={set("status")} className={adminInputClass}>
                {["draft", "published", "archived"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </AdminField>
            <AdminButton variant="emerald" testId="cms-emerald-media-save" onClick={save} disabled={!editing.title}>
              Save Media
            </AdminButton>
          </div>
        )}
      </Drawer>
    </div>
  );
}

/* ----------------------------------------------------- Emerald Settings */

export function EmeraldSettings() {
  const [settings, setSettings] = useState(null);
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/settings")
      .then((r) => setSettings(r.data))
      .catch((e) => setBanner({ status: "error", message: apiError(e) }));
  }, []);

  const cats = settings?.["emerald.nomination_categories_json"] || [];
  const setCats = (next) =>
    setSettings((s) => ({ ...s, "emerald.nomination_categories_json": next }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", {
        values: {
          "emerald.nominations_open": settings["emerald.nominations_open"],
          "emerald.closed_message": settings["emerald.closed_message"],
          "emerald.nomination_categories_json": cats.map((c, i) => ({ ...c, order: i + 1 })),
        },
      });
      setSettings(data);
      setBanner({ status: "ok", message: "Emerald Honours settings saved." });
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
    setSaving(false);
  };

  if (settings === null) return <AdminSpinner />;

  const move = (i, dir) => {
    const next = [...cats];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setCats(next);
  };

  return (
    <div data-testid="cms-emerald-settings">
      <Seo title="Emerald Settings | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead
        title="Emerald Honours Settings"
        subtitle="Control whether nominations are open and manage the nomination categories."
      >
        <AdminButton variant="emerald" testId="cms-emerald-settings-save" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </AdminButton>
      </AdminPageHead>

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <div className="grid gap-5 lg:grid-cols-[1fr_1.2fr]">
        <AdminCard className="space-y-5 p-6">
          <h2 className="font-display text-base font-bold text-bka-navy">Nomination Window</h2>
          <label htmlFor="em-open" className="flex cursor-pointer items-start gap-3 text-sm text-bka-ink">
            <input
              id="em-open"
              data-testid="cms-emerald-nominations-open"
              type="checkbox"
              checked={Boolean(settings["emerald.nominations_open"])}
              onChange={(e) =>
                setSettings((s) => ({ ...s, "emerald.nominations_open": e.target.checked }))
              }
              className="mt-0.5 h-4 w-4 accent-[color:var(--bka-emerald)]"
            />
            <span>
              <strong className="block text-bka-navy">Nominations are open</strong>
              When unchecked, the public nomination form is closed and shows the message below.
            </span>
          </label>
          <AdminField label="Closed Message" htmlFor="em-closed">
            <textarea
              id="em-closed"
              data-testid="cms-emerald-closed-message"
              rows={3}
              value={settings["emerald.closed_message"] || ""}
              onChange={(e) => setSettings((s) => ({ ...s, "emerald.closed_message": e.target.value }))}
              className={adminInputClass}
            />
          </AdminField>
        </AdminCard>

        <AdminCard className="p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="font-display text-base font-bold text-bka-navy">Nomination Categories</h2>
            <AdminButton
              variant="ghost"
              testId="cms-emerald-category-add"
              onClick={() =>
                setCats([
                  ...cats,
                  { id: `category-${Date.now()}`, label: "New category", enabled: true, order: cats.length + 1 },
                ])
              }
            >
              <Plus className="h-4 w-4" /> Add
            </AdminButton>
          </div>
          <p className="mb-4 text-xs text-slate-500">
            These are the official Emerald Honours categories. Edit the name, who the award is for
            and the criteria shown on the public Emerald Honours page. Reorder, disable or add
            categories freely — no code change needed.
          </p>
          <div className="space-y-3">
            {cats.length === 0 && <p className="text-sm text-slate-500">No categories yet.</p>}
            {cats.map((c, i) => {
              const patch = (key) => (value) => {
                const next = [...cats];
                next[i] = { ...c, [key]: value };
                setCats(next);
              };
              return (
                <div
                  key={c.id}
                  data-testid={`cms-emerald-category-${i}`}
                  className="rounded-2xl border border-slate-200 p-4"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex flex-col gap-0.5">
                      <button
                        type="button"
                        aria-label="Move up"
                        data-testid={`cms-emerald-category-up-${i}`}
                        onClick={() => move(i, -1)}
                        className="text-xs text-slate-400 hover:text-bka-emerald"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        aria-label="Move down"
                        data-testid={`cms-emerald-category-down-${i}`}
                        onClick={() => move(i, 1)}
                        className="text-xs text-slate-400 hover:text-bka-emerald"
                      >
                        ▼
                      </button>
                    </div>
                    <label htmlFor={`cat-label-${i}`} className="sr-only">
                      Category name
                    </label>
                    <input
                      id={`cat-label-${i}`}
                      data-testid={`cms-emerald-category-label-${i}`}
                      value={c.label}
                      onChange={(e) => patch("label")(e.target.value)}
                      className={`${adminInputClass} min-w-0 flex-1`}
                    />
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
                      <input
                        type="checkbox"
                        data-testid={`cms-emerald-category-enabled-${i}`}
                        checked={c.enabled !== false}
                        onChange={(e) => patch("enabled")(e.target.checked)}
                        className="h-4 w-4 accent-[color:var(--bka-emerald)]"
                      />
                      Enabled
                    </label>
                    <button
                      type="button"
                      aria-label={`Delete ${c.label}`}
                      data-testid={`cms-emerald-category-delete-${i}`}
                      onClick={() => setCats(cats.filter((_, j) => j !== i))}
                      className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="mt-4 grid gap-4">
                    <AdminField label="Who is this award for?" htmlFor={`cat-recipient-${i}`}>
                      <input
                        id={`cat-recipient-${i}`}
                        data-testid={`cms-emerald-category-recipient-${i}`}
                        value={c.recipient || ""}
                        onChange={(e) => patch("recipient")(e.target.value)}
                        className={adminInputClass}
                        placeholder="Advocate"
                      />
                    </AdminField>
                    <AdminField label="Criteria shown on the website" htmlFor={`cat-criteria-${i}`}>
                      <textarea
                        id={`cat-criteria-${i}`}
                        data-testid={`cms-emerald-category-criteria-${i}`}
                        rows={4}
                        value={c.criteria || ""}
                        onChange={(e) => patch("criteria")(e.target.value)}
                        className={`${adminInputClass} leading-relaxed`}
                      />
                    </AdminField>
                  </div>
                </div>
              );
            })}
          </div>
        </AdminCard>
      </div>
    </div>
  );
}
